/**
 * Sign-in for the contractor portal — a trimmed parallel of `PortalSignIn.tsx`.
 *
 * No first-user bootstrap (a portal account can only ever come from an
 * invite, no exceptions) and no self-service password reset (the contractor
 * portal auth instance has none wired up — see
 * `apps/api/src/contractor-portal-auth.ts`): a contractor who forgets their
 * password asks whoever invited them to send a new link.
 */
import { useEffect, useState } from "react";
import { authErrorMessage } from "./auth-client";
import { contractorPortalSignIn, contractorPortalSignUp } from "./contractor-portal-auth-client";

const MIN_PASSWORD = 10;

interface ContractorPortalInvitePreview {
  companyName: string;
  contractorName: string;
  email: string;
}

/** An invite link is /contractor-portal/invite/<token>; the token never goes in the query string. */
function tokenFromPath(): string | null {
  return window.location.pathname.match(/^\/contractor-portal\/invite\/([^/]+)/)?.[1] ?? null;
}

export function ContractorPortalSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const token = tokenFromPath();
  const [mode, setMode] = useState<"signin" | "signup">(token ? "signup" : "signin");
  const [invite, setInvite] = useState<ContractorPortalInvitePreview | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const creating = mode === "signup";

  // Resolve the link before asking for a password, so an expired invitation
  // says so up front instead of after someone has filled the form in.
  useEffect(() => {
    if (!token) return;
    let live = true;
    fetch(`/api/contractor-portal/invitations/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!live) return;
        if (!res.ok) {
          setInviteError("That invitation has expired or already been used.");
          return;
        }
        const body = (await res.json()) as { invitation: ContractorPortalInvitePreview };
        setInvite(body.invitation);
        // The address is fixed by the invitation — the API rejects any other.
        setEmail(body.invitation.email);
      })
      .catch(() => live && setInviteError("Couldn't check that invitation."));
    return () => {
      live = false;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (creating && password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }

    setBusy(true);
    try {
      const res = creating
        ? await contractorPortalSignUp.email({
            email,
            password,
            name: name || email,
            // Read by the gate in apps/api/src/index.ts, not by Better Auth.
            ...(token ? { invitationToken: token } : {}),
          } as Parameters<typeof contractorPortalSignUp.email>[0])
        : await contractorPortalSignIn.email({ email, password });

      if (res.error) {
        setError(creating ? authErrorMessage(res.error) : "That email and password don't match.");
        return;
      }
      if (token) window.history.replaceState({}, "", "/contractor-portal");
      onSignedIn();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-accent-50/60 to-neutral-50 px-6">
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg shadow-neutral-900/5">
        <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight">
          Contractor Portal
        </h1>

        {invite ? (
          <p className="mt-1 text-sm text-neutral-500">
            You've been invited to view what{" "}
            <span className="text-neutral-900">{invite.contractorName}</span> is owed by{" "}
            <span className="text-neutral-900">{invite.companyName}</span>. Set a password to
            continue.
          </p>
        ) : (
          <p className="mt-1 text-sm text-neutral-500">
            {creating ? "Set up your access." : "Sign in to view what you're owed."}
          </p>
        )}

        {inviteError && (
          <p role="alert" className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {inviteError}
          </p>
        )}

        <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
          {creating && (
            <Field label="Name">
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </Field>
          )}

          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              // Fixed by the invitation: the API rejects a mismatch, so letting
              // it be edited would only produce a confusing rejection.
              readOnly={!!invite}
              disabled={!!invite}
            />
          </Field>

          <Field label="Password">
            <input
              className={inputClass}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={creating ? "new-password" : "current-password"}
            />
            {creating && (
              <p className="mt-1 text-xs text-neutral-500">At least {MIN_PASSWORD} characters.</p>
            )}
          </Field>

          {error && (
            <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || (creating && !!token && !invite)}
            className="mt-1 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-accent-500 disabled:opacity-50"
          >
            {busy ? "Working…" : creating ? "Set up access" : "Sign in"}
          </button>
        </form>

        {!creating && (
          <p className="mt-4 text-sm text-neutral-500">
            Forgot your password? Ask whoever invited you to send a new invitation.
          </p>
        )}

        {/* Without a token there is nothing to create — portal access is by
            invitation only — so the switch is only offered to someone
            holding a link. */}
        {token ? (
          <button
            type="button"
            onClick={() => {
              setMode(creating ? "signin" : "signup");
              setError(null);
            }}
            className="mt-4 text-sm text-neutral-600 underline underline-offset-4 transition-colors hover:text-accent-600"
          >
            {creating ? "Already have access? Sign in" : "Accept the invitation instead"}
          </button>
        ) : (
          <p className="mt-4 text-sm text-neutral-500">
            Access is by invitation only. Ask the company you're working with for a link.
          </p>
        )}
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm outline-none transition-colors focus:border-accent-500 focus:ring-2 focus:ring-accent-500/25 disabled:bg-neutral-100 disabled:shadow-none";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      {children}
    </label>
  );
}
